/**
 * Text handling for the "Número de día hábil" field on the recurring-invoice
 * form.
 *
 * The field used to hold a number directly, which meant there was no way to
 * represent "the box is empty because the user is part-way through retyping
 * it". Deleting the last digit was dropped on the floor and the old digit
 * reappeared, so changing 2 to 3 forced you to type the 3 first and delete the
 * 2 afterwards. The box therefore holds TEXT, and these two functions map it
 * to the committed number.
 */

/** Smallest / largest business day a month can be asked for. */
export const MIN_BUSINESS_DAY_ORDINAL = 1;
export const MAX_BUSINESS_DAY_ORDINAL = 23;

/**
 * The ordinal the text currently represents, or null while it isn't usable —
 * empty, non-numeric, or outside 1–23. Null means "don't commit this yet",
 * NOT "the value is gone": the committed value is kept until valid text
 * replaces it, which is what keeps the próximo-envío preview steady while
 * someone is mid-edit.
 */
export function parseBusinessDayOrdinal(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number.parseInt(trimmed, 10);
  if (value < MIN_BUSINESS_DAY_ORDINAL) return null;
  if (value > MAX_BUSINESS_DAY_ORDINAL) return null;
  return value;
}

/**
 * What the field should show once the user leaves it — every transient state
 * has to resolve to something valid on blur, or the box would sit empty.
 * Digits out of range clamp (99 → 23, 0 → 1); text with no digits at all
 * falls back to the value that was already committed.
 */
export function settleBusinessDayOrdinal(raw: string, committed: number): number {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return committed;
  const value = Number.parseInt(digits, 10);
  if (Number.isNaN(value)) return committed;
  return Math.min(
    MAX_BUSINESS_DAY_ORDINAL,
    Math.max(MIN_BUSINESS_DAY_ORDINAL, value),
  );
}
